import React, { useState, useEffect, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const StockMovement = () => {
  const [movements, setMovements] = useState([])
  const [filteredMovements, setFilteredMovements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })

  useEffect(() => {
    const fetchMovements = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await apiClient.get(API_ENDPOINTS.STOCK_TRANSFERS)
        setMovements(response.data || [])
        setFilteredMovements(response.data || [])
      } catch (err) {
        console.error('Failed to fetch stock movements:', err)
        setError('Unable to load stock movement data. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchMovements()
  }, [])

  useEffect(() => {
    let filtered = [...movements]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(movement => {
        const fields = [
          movement.transfer_type_display,
          movement.status_display,
          movement.reference_number,
          movement.source_warehouse_name,
          movement.source_branch_name,
          movement.destination_warehouse_name,
          movement.destination_branch_name
        ]
        return fields
          .filter(Boolean)
          .some(field => field.toLowerCase().includes(searchLower))
      }
      )
    }

    if (filters.type) {
      filtered = filtered.filter(movement => movement.transfer_type === filters.type)
    }

    if (filters.status) {
      const statusFilter = filters.status.toLowerCase()
      filtered = filtered.filter(movement => (movement.status || '').toLowerCase() === statusFilter)
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(movement =>
        new Date(movement.created_at) >= new Date(filters.dateFrom)
      )
    }

    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo)
      endDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(movement =>
        new Date(movement.created_at) <= endDate
      )
    }

    setFilteredMovements(filtered)
  }, [movements, filters])

  const {
    paginatedItems: paginatedMovements,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredMovements)

  const movementTypes = useMemo(() => {
    const unique = new Map()
    movements.forEach(movement => {
      if (!unique.has(movement.transfer_type)) {
        unique.set(movement.transfer_type, movement.transfer_type_display || movement.transfer_type)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [movements])

  const statusOptions = useMemo(() => {
    const unique = new Map()
    movements.forEach(movement => {
      const value = movement.status
      const label = movement.status_display || value
      if (value && !unique.has(value)) {
        unique.set(value, label || value)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [movements])

  const formatCurrency = (value) =>
    typeof value === 'number'
      ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '$0.00'

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  const isCompleted = (movement) =>
    movement?.status === 'completed' ||
    movement?.status_display?.toLowerCase() === 'completed'

  const handleMovementClick = (movement) => {
    const event = new CustomEvent('navigateToScreen', {
      detail: {
        screen: 'stock-movement-detail',
        params: { movement }
      }
    })
    window.dispatchEvent(event)
  }

  const generatePDF = () => {
    if (filteredMovements.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no stock movements to export. Please adjust your filters.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      const doc = new jsPDF('portrait', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 14
      let yPos = margin
      
      // Add title
      doc.setFontSize(18)
      doc.setTextColor(40, 40, 40)
      doc.text('Stock Movement Report', margin, yPos)
      yPos += 8
      
      // Add filters information
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      const filtersApplied = []
      if (filters.search) filtersApplied.push(`Search: ${filters.search}`)
      if (filters.type) filtersApplied.push(`Type: ${filters.type}`)
      if (filters.status) filtersApplied.push(`Status: ${filters.status}`)
      if (filters.dateFrom) filtersApplied.push(`From: ${filters.dateFrom}`)
      if (filters.dateTo) filtersApplied.push(`To: ${filters.dateTo}`)
      
      if (filtersApplied.length > 0) {
        doc.text(`Filters Applied: ${filtersApplied.join(' | ')}`, margin, yPos)
        yPos += 5
      }
      
      doc.text(`Generated: ${new Date().toLocaleString()} | Total Records: ${filteredMovements.length}`, margin, yPos)
      yPos += 10
      
      // Process each movement with its items
      filteredMovements.forEach((movement, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 80) {
          doc.addPage()
          yPos = margin
        }
        
        // Movement header
        doc.setFontSize(12)
        doc.setTextColor(40, 40, 40)
        doc.setFont(undefined, 'bold')
        doc.text(`Transfer #${movement.reference_number || 'N/A'}`, margin, yPos)
        yPos += 6
        
        // Movement details
        doc.setFontSize(9)
        doc.setTextColor(60, 60, 60)
        doc.setFont(undefined, 'normal')
        doc.text(`Type: ${movement.transfer_type_display || movement.transfer_type || '-'}`, margin, yPos)
        doc.text(`Status: ${movement.status_display || movement.status || '-'}`, margin + 60, yPos)
        yPos += 5
        doc.text(`Source: ${movement.source_warehouse_name || movement.source_branch_name || '-'}`, margin, yPos)
        doc.text(`Destination: ${movement.destination_warehouse_name || movement.destination_branch_name || '-'}`, margin + 60, yPos)
        yPos += 5
        doc.text(`Date: ${formatDateTime(movement.created_at)}`, margin, yPos)
        doc.text(`Total Cost: ${formatCurrency(Number(movement.total_cost) || 0)}`, margin + 60, yPos)
        yPos += 7
        
        // Items table
        const items = Array.isArray(movement.items) ? movement.items : []
        if (items.length > 0) {
          const itemsData = items.map(item => [
            `${item.product_name || '-'}${item.product_sku ? ` (${item.product_sku})` : ''}`,
            item.quantity?.toString() || '0',
            formatCurrency(item.purchase_price),
            formatCurrency(item.total_cost)
          ])
          
          doc.autoTable({
            startY: yPos,
            head: [['Product', 'Quantity', 'Purchase Price', 'Total Cost']],
            body: itemsData,
            theme: 'striped',
            headStyles: {
              fillColor: [66, 139, 202],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: {
              fontSize: 7
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245]
            },
            margin: { top: yPos, left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap'
            },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 30, halign: 'right' },
              2: { cellWidth: 35, halign: 'right' },
              3: { cellWidth: 35, halign: 'right' }
            }
          })
          
          yPos = doc.lastAutoTable.finalY + 5
        } else {
          doc.setFontSize(8)
          doc.setTextColor(120, 120, 120)
          doc.text('No items recorded', margin, yPos)
          yPos += 5
        }
        
        // Separator line between movements
        if (index < filteredMovements.length - 1) {
          doc.setDrawColor(200, 200, 200)
          doc.line(margin, yPos, pageWidth - margin, yPos)
          yPos += 5
        }
      })
      
      // Save PDF
      const fileName = `stock-movement-report-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded',
        text: `Stock movement report has been downloaded as ${fileName}`,
        timer: 2000,
        showConfirmButton: false
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Stock Movement</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Stock Movement</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Stock Movement History
                  </h5>
                  <button
                    className="btn btn-primary"
                    onClick={generatePDF}
                    disabled={loading || filteredMovements.length === 0}
                  >
                    <i className="bi bi-file-pdf me-2"></i>
                    Download PDF
                  </button>
                </div>

                {/* Filters */}
                <div className="row mb-3 g-3">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search movements..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    >
                      <option value="">All Types</option>
                      {movementTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="From Date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="To Date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </div>
                </div>

                {/* Movements Table */}
                {loading ? (
                  <p>Loading stock movement data...</p>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : filteredMovements.length === 0 ? (
                  <p>No stock movements match your filters.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Transfer Type</th>
                          <th>Total Cost</th>
                          <th>Source Warehouse</th>
                          <th>Destination Warehouse</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedMovements.map(movement => (
                          <tr
                            key={movement.id}
                            role="button"
                            onClick={() => handleMovementClick(movement)}
                          >
                            <td>{movement.transfer_type_display || movement.transfer_type}</td>
                            <td>{formatCurrency(Number(movement.total_cost) || 0)}</td>
                            <td>{movement.source_warehouse_name || 'N/A'}</td>
                            <td>{movement.destination_warehouse_name || movement.destination_branch_name || 'N/A'}</td>
                            <td>
                              <span className={`badge ${
                                isCompleted(movement) ? 'bg-success' :
                                movement.status === 'pending' || movement.status_display === 'Pending' ? 'bg-warning text-dark' :
                                'bg-secondary'
                              }`}>
                                {movement.status_display || movement.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="movements"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default StockMovement

